import React, { useEffect, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AuthenticatedClientInfoFields from '../components/AuthenticatedClientInfoFields';
import BookingConfirmationModal from '../components/BookingConfirmationModal';
import { useAuthenticatedBookingProfile } from '../hooks/useAuthenticatedBookingProfile';
import { createBookingRequest } from '../lib/bookingService';
import { formatBudgetRange, handleBudgetKeyDown, normalizeBudgetInput, validateBudgetRange } from '../lib/budget';
import { focusFirstInvalidField } from '../lib/formErrors';
import { validatePhilippineMobile } from '../lib/phoneNumber';

const FieldError = ({ message }) => (
  message ? <p className="text-xs text-rose-300 mt-1">{message}</p> : null
);

export default function ConcertRequestPage() {
  const navigate = useNavigate();
  const { name: requesterName, email: requesterEmail, phone: profilePhone, loading: profileLoading } = useAuthenticatedBookingProfile();
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [createdRequestCode, setCreatedRequestCode] = useState('');
  const [createdBookingId, setCreatedBookingId] = useState('');
  const [formData, setFormData] = useState({
    booking_type: 'concert',
    event_name: '',
    budget_min: '',
    budget_max: '',
    theme: '',
    venue: '',
    event_date: '',
    concert_vendor_choices: {
      venue: 'let_echo_jham',
      technical_rider: 'let_echo_jham',
      musical_director: 'let_echo_jham',
      food: 'let_echo_jham',
    },
    details: '',
    requester_phone: '',
  });

  useEffect(() => {
    if (!profilePhone) return;
    setFormData((prev) => (
      prev.requester_phone === profilePhone ? prev : { ...prev, requester_phone: profilePhone }
    ));
  }, [profilePhone]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nextErrors = {};

    const phoneError = validatePhilippineMobile(formData.requester_phone);
    if (phoneError) nextErrors.requester_phone = phoneError;

    if (!String(formData.event_name || '').trim()) {
      nextErrors.event_name = 'Please enter an event name.';
    }

    const budget = validateBudgetRange(formData.budget_min, formData.budget_max);
    if (budget.error) {
      nextErrors[budget.field || 'budget_min'] = budget.error;
    }

    const requiresVenueDetails = formData.concert_vendor_choices.venue === 'we_have_one';
    if (requiresVenueDetails && !String(formData.venue || '').trim()) {
      nextErrors.venue = 'Please specify the venue.';
    }

    if (!requesterEmail) {
      nextErrors.requester_email = 'Please sign in again to submit a booking.';
    }

    const firstInvalid = ['requester_phone', 'event_name', 'budget_min', 'budget_max', 'venue', 'requester_email']
      .find((field) => nextErrors[field]);
    if (firstInvalid) {
      setFieldErrors(nextErrors);
      focusFirstInvalidField(firstInvalid);
      return;
    }

    setFieldErrors({});
    setLoading(true);
    try {
      const payload = {
        ...formData,
        budget_range: formatBudgetRange(budget.min, budget.max),
        event_date: '',
      };

      const booking = await createBookingRequest(payload);
      setCreatedRequestCode(booking.request_code || '');
      setCreatedBookingId(booking.id || '');
      setShowConfirmation(true);
    } catch (error) {
      console.error('Error:', error);
      alert(error.message || 'Error submitting form');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFieldErrors((prev) => (prev[name] ? { ...prev, [name]: '' } : prev));

    if (name === 'budget_min' || name === 'budget_max') {
      setFormData((prev) => ({
        ...prev,
        [name]: normalizeBudgetInput(value),
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleVendorChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      concert_vendor_choices: {
        ...prev.concert_vendor_choices,
        [name]: value,
      },
    }));
  };

  const requiresVenueDetails = formData.concert_vendor_choices.venue === 'we_have_one';
  const goToCreatedBooking = () => {
    setShowConfirmation(false);
    navigate(createdBookingId ? `/book/progress/${createdBookingId}` : '/book');
  };

  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
    navigate('/book');
  };

  return (
    <div className="max-w-3xl mx-auto">
      <button
        onClick={() => navigate('/book')}
        className="flex items-center gap-2 text-slate-300 hover:text-white transition mb-5 text-sm"
      >
        <ArrowLeft size={18} />
      </button>

      <div className="space-y-6">
        <div className="text-center">
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">Concert Event Booking</h1>
          <p className="text-sm text-slate-400">Provide details for your event, and we&apos;ll handle the rest.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 bg-transparent" noValidate>
          <AuthenticatedClientInfoFields
            name={requesterName}
            email={requesterEmail}
            phone={profilePhone}
            loading={profileLoading}
          />
          {fieldErrors.requester_email ? <FieldError message={fieldErrors.requester_email} /> : null}
          {!profilePhone ? (
          <div>
            <label>Contact Number</label>
            <input
              type="tel"
              name="requester_phone"
              value={formData.requester_phone}
              onChange={handleChange}
              placeholder="Optional — 09XXXXXXXXX or +639XXXXXXXXX"
              aria-invalid={Boolean(fieldErrors.requester_phone)}
            />
            <FieldError message={fieldErrors.requester_phone} />
            <p className="mt-1 text-xs text-slate-400">
              Save a mobile number in Profile Settings to skip this on later bookings.
            </p>
          </div>
          ) : null}

          {/* Event Details */}
          <fieldset className="space-y-4 pb-6 border-b border-white/10">
            <legend className="text-base font-bold text-white">Event Details</legend>
            <div>
              <label>Event Name *</label>
              <input
                type="text"
                name="event_name"
                value={formData.event_name}
                onChange={handleChange}
                required
                placeholder="e.g., Summer Jazz Festival 2026"
                aria-invalid={Boolean(fieldErrors.event_name)}
              />
              <FieldError message={fieldErrors.event_name} />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label>Budget Range *</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input
                    type="text"
                    inputMode="numeric"
                    name="budget_min"
                    value={formData.budget_min}
                    onChange={handleChange}
                    onKeyDown={handleBudgetKeyDown}
                    required
                    placeholder="From: 500000"
                    aria-invalid={Boolean(fieldErrors.budget_min)}
                  />
                  <input
                    type="text"
                    inputMode="numeric"
                    name="budget_max"
                    value={formData.budget_max}
                    onChange={handleChange}
                    onKeyDown={handleBudgetKeyDown}
                    required
                    placeholder="To: 750000"
                    aria-invalid={Boolean(fieldErrors.budget_max || fieldErrors.budget_min)}
                  />
                </div>
                <FieldError message={fieldErrors.budget_min || fieldErrors.budget_max} />
              </div>
            </div>
              <div>
              <div>
                <label>Theme</label>
                <input
                  type="text"
                  name="theme"
                  value={formData.theme}
                  onChange={handleChange}
                  placeholder="e.g., Modern Jazz, Classical"
                />
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <label>Venue Location</label>
                <label className="flex items-center gap-2 cursor-pointer text-slate-300 mb-1">
                  <input
                    type="radio"
                    name="venue"
                    value="let_echo_jham"
                    checked={formData.concert_vendor_choices.venue === 'let_echo_jham'}
                    onChange={handleVendorChange}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Let EchoJham provide</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-red-400 mb-2">
                  <input
                    type="radio"
                    name="venue"
                    value="we_have_one"
                    checked={formData.concert_vendor_choices.venue === 'we_have_one'}
                    onChange={handleVendorChange}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Do you have a venue in mind? Please Specify:</span>
                </label>

                {requiresVenueDetails && (
                  <>
                    <input
                      type="text"
                      id="venue"
                      name="venue"
                      value={formData.venue}
                      onChange={handleChange}
                      required={requiresVenueDetails}
                      placeholder="Specify here"
                      aria-invalid={Boolean(fieldErrors.venue)}
                    />
                    <FieldError message={fieldErrors.venue} />
                  </>
                )}
              </div>
            </div>
          </fieldset>

          {/* Vendor Options */}
          <fieldset className="space-y-4 pb-6 border-b border-white/10">
            <legend className="text-base font-bold text-white">Let EchoJham Help With</legend>
            {[
              { key: 'technical_rider', label: 'Technical Rider' },
              { key: 'musical_director', label: 'Musical Director' },
              { key: 'food', label: 'Food' },
            ].map(item => (
              <div key={item.key} className="grid md:grid-cols-2 gap-3 border border-white/10 rounded-xl p-4 bg-white/[0.02]">
                <label className="md:col-span-2">{item.label}</label>
                <label className="flex items-center gap-2 cursor-pointer text-slate-300">
                  <input
                    type="radio"
                    name={item.key}
                    value="let_echo_jham"
                    checked={formData.concert_vendor_choices[item.key] === 'let_echo_jham'}
                    onChange={handleVendorChange}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Let EchoJham provide</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-slate-300">
                  <input
                    type="radio"
                    name={item.key}
                    value="we_have_one"
                    checked={formData.concert_vendor_choices[item.key] === 'we_have_one'}
                    onChange={handleVendorChange}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">We already have one</span>
                </label>
              </div>
            ))}
          </fieldset>

          {/* Additional Info */}
          <fieldset className="space-y-4">
            <legend className="text-base font-bold text-white">Additional Requirements</legend>
            <div>
              <textarea
                name="details"
                value={formData.details}
                onChange={handleChange}
                rows="5"
                placeholder="Any other details, special requests, or notes about your concert event..."
              />
            </div>
          </fieldset>

          {/* Submit */}
          <div className="flex gap-4 pt-6 border-t border-white/10">
            <button
              type="button"
              onClick={() => navigate('/book')}
              className="flex-1 py-3 rounded-xl font-semibold border border-white/20 text-slate-200 hover:bg-white/10"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || profileLoading || !requesterEmail}
              className="flex-1 py-3 rounded-xl font-semibold bg-[#8b0000] hover:bg-[#6f0000] text-white disabled:opacity-50"
            >
              {loading ? 'Submitting...' : 'Submit Concert Request'}
            </button>
          </div>
        </form>
      </div>

      <BookingConfirmationModal
        open={showConfirmation}
        requestCode={createdRequestCode}
        onClose={handleCloseConfirmation}
        onViewProcess={goToCreatedBooking}
      />
    </div>
  );
}
